// src/annihilation/oldFoldGeometry.js
// ============================================================
//  ANNIHILATION — THE OLD FOLD · shared geometry
//
//  The ONE source of truth for WHERE things stand in the home
//  village. Both renderers read this file:
//    · Plaza.js       — the 2D tile world (the shipped renderer)
//    · OldFold3D.jsx  — the 3D renderer (the swap)
//
//  Per the 3D-conversion contract: the 3D view must NOT own its
//  own copy of the world, or the two drift. It discovers the
//  village from this data, exactly as the 2D view does.
//
//  Vyren pattern: the `id` strings here are INTERNAL and never
//  change. Nothing in this file is a display string — display
//  names live in villageData.js. This file owns geometry only.
//
//  These values were extracted verbatim from Plaza.js (they were
//  living inside the component). Moving them here is non-breaking:
//  identical numbers, one home instead of two.
// ============================================================

// ── Landscape / desktop map ──────────────────────────────────
export const COLS = 34;
export const ROWS = 20;
export const SPAWN = { x: 14, y: 13 };

// The two ways OUT of the village. The Gate carries the co-op
// ready-up flow; Pierre keeps its IE-universal id.
export const PORTALS = [
  { id: "gate",   label: "The Gate",            subtitle: "The Outskirts · Co-op", color: "#e8a33d", dungeonId: 900, spriteKey: "portal_gate",   x: 12, y: 3 },
  { id: "pierre", label: "The Unstable Pierre", subtitle: "Cross-Game Hub",        color: "#06b6d4", dungeonId: -1,  spriteKey: "portal_pierre", x: 22, y: 3 },
];

// The seven walk-in buildings (the Gate is a portal, above).
export const BUILDING_GEO = {
  bonfire:     { x: 17, y: 10, rx: 2, ry: 2 },
  marketplace: { x: 5,  y: 15, rx: 3, ry: 2, minX: 1,  maxX: 9 },
  forge:       { x: 29, y: 15, rx: 3, ry: 2, minX: 25 },
  trainer:     { x: 5,  y: 7,  rx: 3, ry: 2, minX: 1,  maxX: 9 },
  registrar:   { x: 29, y: 7,  rx: 3, ry: 2, minX: 25 },
  bulletin:    { x: 24, y: 11, rx: 2, ry: 2, minX: 22, maxX: 26 },
  garden:      { x: 10, y: 17, rx: 3, ry: 1, minX: 6,  maxX: 14 },
};

// Building accent colors (used by both renderers).
export const BUILDING_COLORS = {
  bonfire: "#e8a33d", marketplace: "#c9a84c", forge: "#f0946a",
  trainer: "#8fb8d8", registrar: "#b8a0e8", bulletin: "#a8c686",
  garden: "#7fe8c8",
};

// Villagers standing in the square (walk-up dialogue).
export const VILLAGER_GEO = {
  maerie: { x: 9,  y: 13, rx: 2, ry: 2 },
  sigfer: { x: 9,  y: 6,  rx: 2, ry: 2, maxX: 11 },
  vel:    { x: 17, y: 6,  rx: 2, ry: 2, minX: 15, maxX: 19 },
};

// ── Portrait / compact mobile map ────────────────────────────
export const P_COLS = 20;
export const P_ROWS = 12;
export const P_SPAWN = { x: 12, y: 8 };

export const P_PORTALS = [
  { id: "gate",   label: "The Gate",            subtitle: "The Outskirts · Co-op", color: "#e8a33d", dungeonId: 900, spriteKey: "portal_gate",   x: 6,  y: 2 },
  { id: "pierre", label: "The Unstable Pierre", subtitle: "Cross-Game Hub",        color: "#06b6d4", dungeonId: -1,  spriteKey: "portal_pierre", x: 13, y: 2 },
];

export const P_BUILDING_GEO = {
  trainer:     { x: 2,  y: 7,  rx: 1, ry: 1, maxX: 4 },
  bonfire:     { x: 6,  y: 7,  rx: 1, ry: 1, minX: 5,  maxX: 8 },
  forge:       { x: 10, y: 7,  rx: 1, ry: 1, minX: 9,  maxX: 12 },
  registrar:   { x: 14, y: 7,  rx: 1, ry: 1, minX: 13 },
  marketplace: { x: 3,  y: 10, rx: 2, ry: 1, maxX: 6 },
  bulletin:    { x: 9,  y: 10, rx: 2, ry: 1, minX: 7,  maxX: 11 },
  garden:      { x: 15, y: 10, rx: 2, ry: 1, minX: 13 },
};

export const P_VILLAGER_GEO = {
  maerie: { x: 3,  y: 4, rx: 1, ry: 1 },
  sigfer: { x: 9,  y: 4, rx: 1, ry: 1 },
  vel:    { x: 15, y: 4, rx: 1, ry: 1 },
};

// ── Derived helpers ──────────────────────────────────────────
// A flat list of buildings with geometry, for renderers that want
// to iterate. Display names are joined in by the renderer from
// villageData; here we expose only ids + coords + color.
export function buildingList(portrait = false) {
  const geo = portrait ? P_BUILDING_GEO : BUILDING_GEO;
  return Object.entries(geo).map(([id, g]) => ({
    id, color: BUILDING_COLORS[id] || "#c9a84c", ...g,
  }));
}

export function villagerList(portrait = false) {
  const geo = portrait ? P_VILLAGER_GEO : VILLAGER_GEO;
  return Object.entries(geo).map(([id, g]) => ({ id, ...g }));
}

export function portalList(portrait = false) {
  return portrait ? P_PORTALS : PORTALS;
}
